import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { fetchStaffList } from 'services/fetchStaffList';
import { CreateStaff } from 'types/staff';
import { getDefaultCompanyId } from 'utils/helpers';

interface Staff {
    username: string;
    firstName: string;
    lastName: string;
    name: string;
    uuid: string;
    status: string;
    createdDate: string;
    updatedDate: string;
}
interface StaffList {
    activeRecords: Staff[];
    inactiveRecords: Staff[];
    pendingRecords: Staff[];
    deletedRecords: Staff[];
    loading: boolean;
    error: string | null | boolean;
}

export type SetStatus = 'ACTIVE' | 'DELETED' | 'INACTIVE' | 'ALL' | 'PENDING';

const initialState: StaffList = {
    activeRecords: [],
    inactiveRecords: [],
    pendingRecords: [],
    deletedRecords: [],
    loading: false,
    error: null,
};

export const getStaffList = createAsyncThunk('active/getStaffList', async ({ setStatus }: { setStatus: SetStatus }, { getState }) => {
    const state: any = await getState();
    const defaultCompanyId = getDefaultCompanyId(state);
    if (!defaultCompanyId) {
        return;
    }
    const sortedEmployees = await fetchStaffList(defaultCompanyId, setStatus);
    return { type: setStatus, list: sortedEmployees || [] };
});

export const createStaff = createAsyncThunk('staff/createStaff', async ({ formValues }: { formValues: CreateStaff }, { getState }) => {
    const [firstName, lastName] = formValues?.fullName?.split(' ') || '';
    const state: any = await getState();
    const defaultCompanyId = getDefaultCompanyId(state);
    if (!defaultCompanyId) {
        return;
    }
    const { fullName, ...formData } = formValues;

    // adding new key as username because back end require the username but not in figma
    const _formValues = {
        ...formData,
        firstName,
        lastName,
        username: formData?.email,
    };
    // Todo: When we change the URL for Old Glory, we need to check if the API accepts the fullName, because the UI only has a Name field
    const response: any = await client.post('/merchant/createEmployee', _formValues, {
        companyId: defaultCompanyId,
        timezone: 'America/Chicago',
    });
    return response;
});

export const removeStaff = createAsyncThunk('staff/removeStaff', async (uuid: string, { getState }) => {
    const state: any = await getState();
    const companyId = getDefaultCompanyId(state);
    if (!companyId) {
        return;
    }
    const response: any = await client.delete(`/merchant/removeEmployee/${uuid}`, {
        headers: {
            companyId,
        },
    });
    if (response.code === 200) {
        notify('User Removed successfully', 'success');
    }
    return response;
});

export const fetchUserDetails = createAsyncThunk(
    'staffDetail/fetchUserDetails',
    async ({ username }: { username: string }, { getState }) => {
        const state: any = getState();
        const companyId = getDefaultCompanyId(state);
        const response: any = await client.get('/user/getUser', {
            headers: {
                username,
                companyId,
            },
        });
        const { msg, statusCode, code, ...staffDetails } = response;
        return staffDetails;
    }
);

const staffSlice = createSlice({
    name: 'activeStaff',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getStaffList.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getStaffList.fulfilled, (state, action) => {
            const { payload } = action;
            state.loading = false;
            if (!payload) return;
            const { list, type } = payload;
            switch (type) {
                case 'ACTIVE':
                    state.activeRecords = list;
                    break;
                case 'INACTIVE':
                    state.inactiveRecords = list;
                    break;
                case 'PENDING':
                    state.pendingRecords = list;
                    break;
                case 'DELETED':
                    state.deletedRecords = list;
                    break;
                default:
                    state.activeRecords = list;
            }
        });
        builder.addCase(getStaffList.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export default staffSlice.reducer;
